// frontend/src/pages/admin/AdminProviders.js
import React, { useState, useEffect, useMemo } from 'react';
import { serviceOpsApi } from '../../api/serviceOps';
import { zonesApi } from '../../api/zones';
import { usersApi } from '../../api/users';
import { 
    FaPlus, FaTrash, FaTimes, FaStore, FaUserCog, FaUserTie, 
    FaMapMarkerAlt, FaPhone, FaCheck, FaBan, FaPause, FaPlay, 
    FaSpinner, FaCheckCircle, FaTruck, FaCar, FaBox, FaEnvelope, FaIdCard, FaInfoCircle 
} from 'react-icons/fa';

const initialFormState = {
    type: 'station',
    name: '',
    user_id: '',
    phone: '',
    email: '',
    skills: '',
    location_lga_id: '',
    state: '', 
    address: '',
    is_active: true
};

// --- PRE-MADE REASONS ---
const PREMADE_REJECTION_REASONS = [
    "Identity verification photo is unclear or invalid.",
    "Business address could not be verified on Google Maps.",
    "Business description is too vague or incomplete.",
    "Duplicate application found in our system.",
    "Contact information provided is unreachable.",
    "Does not meet minimum experience requirements.",
    "Selected Zone does not match the provided address."
];

function AdminProviders() {
    const [providers, setProviders] = useState([]);
    const [applications, setApplications] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('providers');
    const [filterType, setFilterType] = useState('all');

    // Create Provider Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);

    // Review Application Modal State
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [reviewApp, setReviewApp] = useState(null);
    const [rejectMode, setRejectMode] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processingReview, setProcessingReview] = useState(false);

    // Verification State
    const [isAddressVerified, setIsAddressVerified] = useState(false);
    const [verifyingAddress, setVerifyingAddress] = useState(false);
    const [verifiedAddressText, setVerifiedAddressText] = useState('');
    const [verifiedSource, setVerifiedSource] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [providersData, appsData, lgasData, usersData] = await Promise.all([
                serviceOpsApi.getAllProviders(),
                serviceOpsApi.getApplications('pending'),
                zonesApi.getAllLgas(),
                usersApi.getAllUsersAdmin()
            ]);
            setProviders(providersData);
            setApplications(appsData);
            setLgas(lgasData);
            setUsers(usersData);
        } catch (err) {
            console.error(err);
            setError("Failed to load data.");
        } finally {
            setLoading(false);
        }
    };

    const groupedLgas = useMemo(() => {
        return lgas.reduce((acc, lga) => {
            if (!lga.state || !lga.lga) return acc;
            const stateKey = lga.state.trim();
            if (!acc[stateKey]) acc[stateKey] = [];
            acc[stateKey].push(lga);
            return acc;
        }, {});
    }, [lgas]);

    const availableLgas = formData.state ? groupedLgas[formData.state] || [] : [];

    const filteredProviders = providers.filter(p => filterType === 'all' || p.type === filterType);

    // --- HANDLERS ---

    const handleToggleStatus = async (provider) => {
        const action = provider.is_active ? 'suspend' : 'activate';
        if (!window.confirm(`${action.toUpperCase()} this provider?`)) return;

        try {
            await serviceOpsApi.updateProviderStatus(provider.id, action);
            setProviders(prev => prev.map(p => 
                p.id === provider.id ? { ...p, is_active: !p.is_active } : p
            ));
        } catch (err) {
            alert("Failed to update status.");
        }
    };

    // Open the Review Modal
    const openReviewModal = (app) => {
        setReviewApp(app);
        setRejectMode(false);
        setRejectionReason('');
        setIsReviewModalOpen(true);
    };

    const closeReviewModal = () => {
        setIsReviewModalOpen(false);
        setReviewApp(null);
        setRejectMode(false);
    };

    const handleApproveApp = async () => {
        if (!window.confirm("Are you sure you want to APPROVE this application?")) return;
        
        setProcessingReview(true);
        try {
            await serviceOpsApi.reviewApplication(reviewApp.id, 'approve');
            alert("Application Approved!");
            closeReviewModal();
            fetchData();
        } catch (err) {
            alert("Failed to approve application.");
        } finally {
            setProcessingReview(false);
        }
    };

    const handleRejectApp = async () => {
        console.log("Rejecting app:", reviewApp?.id, "Reason:", rejectionReason);
        
        if (!rejectionReason || !rejectionReason.trim()) {
            alert("Please provide a reason for rejection.");
            return;
        }

        setProcessingReview(true);
        try {
            await serviceOpsApi.reviewApplication(reviewApp.id, 'reject', rejectionReason);
            alert("Application Rejected.");
            closeReviewModal();
            fetchData();
        } catch (err) {
            console.error("Rejection failed:", err);
            alert("Failed to reject application.");
        } finally {
            setProcessingReview(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        
        if (['state', 'location_lga_id', 'address'].includes(name)) {
            setIsAddressVerified(false);
            setVerifiedAddressText('');
            setVerifiedSource('');
        }
    };

    const handleVerifyAddress = async () => {
        if (!formData.state || !formData.location_lga_id || !(formData.address || '').trim()) {
            alert("Please fill in State, LGA, and Address first.");
            return;
        }

        setVerifyingAddress(true);
        try {
            const lgaName = lgas.find(l => l.id === formData.location_lga_id)?.lga || '';

            // The State MUST be sent - without it the server cannot check the
            // address is in the right state. The street goes on its own; the
            // server adds the LGA and State itself.
            const response = await serviceOpsApi.verifyAddress({
                address: formData.address.trim(),
                lga: lgaName,
                state: formData.state
            });

            if (response.verified) {
                setIsAddressVerified(true);
                setVerifiedAddressText(`${response.found_address} - ${response.match_type}`);
                setVerifiedSource(response.source || '');
            } else {
                alert(response.message || "We could not verify this address. Please check it and try again.");
                setIsAddressVerified(false);
                setVerifiedAddressText('');
                setVerifiedSource('');
            }
        } catch (err) {
            alert(err?.data?.message || "Verification service unavailable.");
        } finally {
            setVerifyingAddress(false);
        }
    };

    const handleUserSelect = (e) => {
        const userId = e.target.value;
        const selectedUser = users.find(u => u.id === userId);
        
        if (selectedUser) {
            setIsAddressVerified(false);
            setVerifiedAddressText('');
            setVerifiedSource('');

            setFormData(prev => ({
                ...prev,
                user_id: userId,
                name: prev.name || selectedUser.full_name || '',
                email: prev.email || selectedUser.email || '',
                phone: prev.phone || selectedUser.phone || '',
                address: selectedUser.address || prev.address,
                state: selectedUser.state || prev.state,
                location_lga_id: selectedUser.lga_id || prev.location_lga_id,
                skills: selectedUser.skills || prev.skills
            }));
        } else {
            setFormData(prev => ({ ...prev, user_id: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isAddressVerified) {
            if (!window.confirm("Address has NOT been verified. Proceed anyway?")) return;
        }

        setSaving(true);
        try {
            const payload = {
                ...formData,
                skills: formData.skills.split(',').map(s => s.trim()).filter(s => s !== '')
            };
            if (!payload.user_id) delete payload.user_id;
            if (!payload.email) delete payload.email;
            delete payload.state;

            await serviceOpsApi.createProvider(payload);
            await fetchData();
            setIsModalOpen(false);
            setFormData(initialFormState);
            setIsAddressVerified(false);
        } catch (err) {
            alert(`Error: ${err.message || "Failed to create provider"}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("PERMANENTLY DELETE this provider?")) {
            try {
                await serviceOpsApi.deleteProvider(id);
                setProviders(prev => prev.filter(p => p.id !== id));
            } catch (err) {
                alert("Failed to delete provider.");
            }
        }
    };

    const getLgaName = (id) => {
        const lga = lgas.find(l => l.id === id);
        return lga ? `${lga.lga}, ${lga.state}` : 'Unknown Location';
    };

    const getTypeIcon = (type) => {
        switch(type) {
            case 'station': return <FaStore className="text-blue-600" />;
            case 'freelancer': return <FaUserCog className="text-green-600" />;
            case 'expert': return <FaUserTie className="text-purple-600" />;
            case 'parts_seller': return <FaBox className="text-orange-600" />;
            case 'car_dealer': return <FaCar className="text-red-600" />;
            case 'logistics_partner': return <FaTruck className="text-yellow-600" />;
            default: return null;
        }
    };

    if (loading) return <div className="p-8 text-center">Loading Service Network...</div>;

    return (
        <div className="w-full">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Service Network</h1>
                <button 
                    onClick={() => setIsModalOpen(true)} 
                    className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded flex items-center gap-2 whitespace-nowrap"
                >
                    <FaPlus /> Add Provider
                </button>
            </div>

            {/* MAIN TABS */}
            <div className="flex gap-4 mb-6 border-b border-gray-200">
                <button 
                    onClick={() => setActiveTab('providers')}
                    className={`pb-2 px-4 font-medium ${activeTab === 'providers' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
                >
                    Active Providers
                </button>
                <button 
                    onClick={() => setActiveTab('applications')}
                    className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'applications' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
                >
                    Pending Applications
                    {applications.length > 0 && (
                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{applications.length}</span>
                    )}
                </button>
            </div>

            {/* --- VIEW: ACTIVE PROVIDERS --- */}
            {activeTab === 'providers' && (
                <>
                    <div className="flex gap-2 mb-6 overflow-x-auto whitespace-nowrap scrollbar-hide">
                        {/* ADDED 'car_dealer' TO THIS LIST */}
                        {['all', 'station', 'freelancer', 'expert', 'parts_seller', 'car_dealer', 'logistics_partner'].map(type => (
                            <button
                                key={type}
                                onClick={() => setFilterType(type)}
                                className={`px-4 py-2 text-sm font-medium capitalize rounded-full transition-colors flex-shrink-0 ${
                                    filterType === type 
                                    ? 'bg-gray-800 text-white' 
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {type === 'all' ? 'All' : type.replace('_', ' ') + 's'}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredProviders.map(provider => (
                            <div key={provider.id} className={`bg-white rounded-lg shadow-sm border p-5 hover:shadow-md transition-shadow min-w-0 ${!provider.is_active ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="bg-gray-50 p-3 rounded-full text-xl flex-shrink-0">
                                            {getTypeIcon(provider.type)}
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="font-bold text-gray-800 text-lg truncate">{provider.name}</h3>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs uppercase font-bold text-gray-400">{provider.type.replace('_', ' ')}</span>
                                                {!provider.is_active && <span className="text-xs bg-red-100 text-red-600 px-2 rounded font-bold">SUSPENDED</span>}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => handleToggleStatus(provider)}
                                            className={`p-2 rounded ${provider.is_active ? 'text-yellow-600 hover:bg-yellow-100' : 'text-green-600 hover:bg-green-100'}`}
                                            title={provider.is_active ? "Suspend" : "Activate"}
                                        >
                                            {provider.is_active ? <FaPause /> : <FaPlay />}
                                        </button>
                                        <button onClick={() => handleDelete(provider.id)} className="text-gray-400 hover:text-red-600 flex-shrink-0 p-2">
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2 text-sm text-gray-600 mb-4">
                                    <p className="flex items-center gap-2 truncate">
                                        <FaMapMarkerAlt className="text-gray-400 flex-shrink-0" /> 
                                        <span className="truncate">{getLgaName(provider.location_lga_id)}</span>
                                    </p>
                                    <p className="flex items-center gap-2 truncate">
                                        <FaPhone className="text-gray-400 flex-shrink-0" /> 
                                        <span className="truncate">{provider.phone}</span>
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* --- VIEW: PENDING APPLICATIONS --- */}
            {activeTab === 'applications' && (
                <div className="space-y-4">
                    {applications.length === 0 ? (
                        <div className="text-center py-10 text-gray-500">No pending applications.</div>
                    ) : (
                        applications.map(app => (
                            <div key={app.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                                <div className="flex flex-col md:flex-row justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <h3 className="text-lg font-bold text-gray-800">{app.business_name}</h3>
                                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full uppercase font-bold">
                                                {app.type.replace('_', ' ')} Application
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-1">
                                            <strong>Applicant:</strong> {app.user_name} ({app.user_email})
                                        </p>
                                        <p className="text-sm text-gray-600 mb-1">
                                            <strong>Location:</strong> {getLgaName(app.location_lga_id)}
                                        </p>
                                    </div>

                                    <div className="flex items-center">
                                        <button 
                                            onClick={() => openReviewModal(app)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded font-bold shadow-sm"
                                        >
                                            Review Application
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* --- REVIEW APPLICATION MODAL --- */}
            {isReviewModalOpen && reviewApp && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-md flex flex-col animate-fadeInUp overflow-hidden">
                        
                        {/* Header */}
                        <div className="flex justify-between items-center p-4 border-b bg-gray-50 flex-shrink-0">
                            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <FaIdCard className="text-blue-600" /> Application Review
                            </h3>
                            <button onClick={closeReviewModal} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-grow max-h-[70vh]">
                            
                            {/* KYC Photo */}
                            <div className="flex justify-center mb-6">
                                <div className="w-32 h-32 rounded-full border-4 border-gray-200 overflow-hidden shadow-inner bg-gray-100">
                                    {reviewApp.kyc_photo_url ? (
                                        <img src={reviewApp.kyc_photo_url} alt="KYC" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs text-center p-2">
                                            No Photo Provided
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Applicant Details */}
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
                                <h4 className="text-xs font-bold text-blue-800 uppercase mb-2 flex items-center gap-1">
                                    <FaUserTie /> Applicant
                                </h4>
                                <p className="text-sm text-gray-800"><strong>Name:</strong> {reviewApp.user_name}</p>
                                <p className="text-sm text-gray-800 break-all"><strong>Email:</strong> {reviewApp.user_email}</p>
                                <p className="text-sm text-gray-800"><strong>Phone:</strong> {reviewApp.user_phone}</p>
                            </div>

                            {/* Business Details */}
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase">Business Name</label>
                                    <p className="text-gray-800 font-medium">{reviewApp.business_name}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase">Type</label>
                                    <p className="text-gray-800 capitalize">{reviewApp.type.replace('_', ' ')}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase">Location</label>
                                    <p className="text-gray-800">{getLgaName(reviewApp.location_lga_id)}</p>
                                    <p className="text-xs text-gray-500">{reviewApp.address}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase">Description</label>
                                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded border">{reviewApp.description}</p>
                                </div>
                            </div>

                            {/* Rejection Input */}
                            {rejectMode && (
                                <div className="mt-6 animate-fadeInUp">
                                    <label className="block text-sm font-bold text-red-600 mb-1">Reason for Rejection *</label>
                                    
                                    {/* Quick Select Dropdown */}
                                    <select 
                                        className="w-full max-w-full p-2 border border-red-300 rounded mb-2 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-red-500 outline-none"
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === 'Others') {
                                                setRejectionReason(''); // Clear to let them type
                                            } else {
                                                setRejectionReason(val); // Auto-fill
                                            }
                                        }}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>-- Select a common reason (Optional) --</option>
                                        {PREMADE_REJECTION_REASONS.map((reason, idx) => (
                                            <option key={idx} value={reason}>{reason}</option>
                                        ))}
                                        <option value="Others">Others (Specify below)</option>
                                    </select>

                                    <textarea 
                                        className="w-full p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 outline-none bg-red-50"
                                        rows="3"
                                        placeholder="Type the specific reason for rejection here..."
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                    ></textarea>
                                </div>
                            )}
                        </div>

                        {/* Footer Actions */}
                        <div className="p-4 border-t bg-gray-50 flex justify-between gap-3 flex-shrink-0">
                            {!rejectMode ? (
                                <>
                                    <button 
                                        type="button"
                                        onClick={() => setRejectMode(true)}
                                        className="flex-1 bg-red-100 text-red-600 py-2 rounded font-bold hover:bg-red-200 flex items-center justify-center gap-2"
                                    >
                                        <FaBan /> Reject
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={handleApproveApp}
                                        disabled={processingReview}
                                        className="flex-1 bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700 flex items-center justify-center gap-2 shadow-sm"
                                    >
                                        {processingReview ? <FaSpinner className="animate-spin" /> : <FaCheck />} Approve Application
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button 
                                        type="button"
                                        onClick={() => setRejectMode(false)}
                                        className="flex-1 bg-gray-200 text-gray-700 py-2 rounded font-bold hover:bg-gray-300"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={handleRejectApp}
                                        disabled={processingReview}
                                        className="flex-1 bg-red-600 text-white py-2 rounded font-bold hover:bg-red-700 flex items-center justify-center gap-2 shadow-sm"
                                    >
                                        {processingReview ? <FaSpinner className="animate-spin" /> : "Confirm Rejection"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* --- CREATE PROVIDER MODAL (Existing) --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h3 className="text-xl font-bold">Register Service Provider</h3>
                            <button onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
                            {/* Type Selection */}
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Provider Type *</label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {['station', 'freelancer', 'expert', 'parts_seller', 'car_dealer', 'logistics_partner'].map(t => (
                                        <label key={t} className={`border rounded p-3 cursor-pointer flex flex-col items-center gap-2 ${formData.type === t ? 'border-primary bg-blue-50 text-primary' : 'border-gray-200'}`}>
                                            <input 
                                                type="radio" name="type" value={t} 
                                                checked={formData.type === t} 
                                                onChange={handleInputChange} 
                                                className="hidden" 
                                            />
                                            {getTypeIcon(t)}
                                            <span className="capitalize font-bold text-xs text-center">{t.replace('_', ' ')}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Link User */}
                            <div className="bg-gray-50 p-4 rounded border">
                                <label className="block text-xs font-bold text-gray-600 mb-1">Link to User Account (Optional)</label>
                                <select 
                                    name="user_id" 
                                    value={formData.user_id} 
                                    onChange={handleUserSelect} 
                                    className="w-full p-2 border rounded bg-white"
                                >
                                    <option value="">-- Select a Registered User --</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.roles?.join(', ')})</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Name *</label>
                                    <input name="name" value={formData.name} onChange={handleInputChange} required className="w-full p-2 border rounded" placeholder="e.g. Total Auto Care" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Phone *</label>
                                    <input name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full p-2 border rounded" />
                                </div>
                            </div>

                            {/* LOCATION & VERIFICATION */}
                            <div className="bg-blue-50 p-4 rounded border border-blue-100">
                                <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2"><FaMapMarkerAlt /> Location & Verification</h4>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">State *</label>
                                        <select name="state" value={formData.state} onChange={handleInputChange} required className="w-full p-2 border rounded bg-white">
                                            <option value="">Select State</option>
                                            {Object.keys(groupedLgas).sort().map(state => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">LGA *</label>
                                        <select name="location_lga_id" value={formData.location_lga_id} onChange={handleInputChange} required disabled={!formData.state} className="w-full p-2 border rounded bg-white disabled:bg-gray-100">
                                            <option value="">Select LGA</option>
                                            {availableLgas.map(lga => (
                                                <option key={lga.id} value={lga.id}>{lga.lga}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="mb-2">
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Street Address *</label>
                                    <div className="flex gap-2">
                                        <input name="address" value={formData.address} onChange={handleInputChange} required className="flex-grow p-2 border rounded" placeholder="e.g. 5 Adejuwon Street" />
                                        <button 
                                            type="button"
                                            onClick={handleVerifyAddress}
                                            disabled={verifyingAddress || isAddressVerified}
                                            className={`px-4 py-2 rounded text-sm font-bold transition-colors ${isAddressVerified ? 'bg-green-100 text-green-700 cursor-default' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                        >
                                            {verifyingAddress ? <FaSpinner className="animate-spin" /> : isAddressVerified ? 'Verified' : 'Verify'}
                                        </button>
                                    </div>
                                </div>

                                {isAddressVerified && (
                                    <div className="text-green-600 text-xs font-bold flex items-center gap-1 mt-2 bg-white p-2 rounded border border-green-200">
                                        <FaCheckCircle /> 
                                        <span>Location verified{verifiedSource ? ` via ${verifiedSource}` : ''}: {verifiedAddressText}</span>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Skills (Comma separated)</label>
                                <input name="skills" value={formData.skills} onChange={handleInputChange} placeholder="e.g. Painting, BMW Engine" className="w-full p-2 border rounded" />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                                <button type="submit" disabled={saving} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">
                                    {saving ? 'Saving...' : 'Create Provider'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminProviders;